import { initializeApp } from "firebase/app";
import { 
  getFirestore, 
  doc, 
  setDoc, 
  addDoc, 
  collection,
  serverTimestamp, 
  getDoc,
  query,
  where,
  onSnapshot,
  updateDoc
} from "firebase/firestore";
import { getFunctions, httpsCallable } from 'firebase/functions';

interface StudentMessagePayload {
  userId: string;
  from: string;
  replyTo: string;
  studentId: string;
  studentEmail: string;
  subject: string;
  message: string;
}

// Your web app's Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyDiN2M4LA0N6HP3xX3GctyrT67LZCFCX4M",
  authDomain: "thepoppyapp.firebaseapp.com",
  projectId: "thepoppyapp",
  storageBucket: "thepoppyapp.firebasestorage.app",
  messagingSenderId: "689732906452",
  appId: "1:689732906452:web:04dfaeb4265d436ca9ba41",
  measurementId: "G-MFW15VVB29"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const functions = getFunctions(app, 'us-central1');

export const sendContactMessage = async (data: { name: string; email: string; subject: string; message: string; userId: string}) => {
  try {
    const sendMail = httpsCallable(functions, 'sendContactEmail');
    const result = await sendMail(data);
    return result;
  } catch (error) {
    //console.error("Erreur lors de l'envoi du message de contact:", error);
    throw error;
  }
};

export const updateUserActivity = async (userId: string) => {
  try {
    const uid = String(userId);
    const userRef = doc(db, "userActivity", uid);

    // Crée ou met à jour le document
    await setDoc(userRef, {
      lastActivity: serverTimestamp(),
      isOnline: true
    }, { merge: true });

    // Timeout pour marquer online après 5 min
    setTimeout(async () => {
      try {
        await setDoc(userRef, { 
          lastActivity: serverTimestamp(),
          isOnline: true
         }, { merge: true });
      } catch (err) {
        console.error("Error marking user offline:", err);
      }
    }, 300000);

  } catch (error) {
    //console.error("Error updating user activity:", error);
  }
};

export const getUserActivity = async (userId: string) => {
  try {
    const userRef = doc(db, "userActivity", userId);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      return userSnap.data();
    } else {
      //console.log("No such document!");
      return null;
    }
  } catch (error) {
    //console.error("Error getting user activity:", error);
    return null;
  }
};

export const sendStudentMessage = async (payload: StudentMessagePayload) => {
  try {
    const fromKey = (payload.from || '').trim().toLowerCase();
    const toKey = (payload.studentEmail || '').trim().toLowerCase();
    await addDoc(collection(db, 'mail'), {
      to: payload.studentEmail,
      from: payload.from,
      replyTo: payload.replyTo,
      status: "pending",
      message: {
        subject: payload.subject,
        html: payload.message,
        text: payload.message,
      },
      createdAt: serverTimestamp(),
      type: 'student', // Type pour identifier les messages aux élèves
      studentId: payload.studentId, // ID de l'élève
      userId: payload.userId, // ID du coach qui envoie (user.id)
      participants: [fromKey, toKey],
      conversationId: [fromKey, toKey].sort().join('|'),
      readBy: [fromKey],
    });
  } catch (error) {
    console.error("Erreur lors de l'envoi du message à l'élève:", error);
    // Il est préférable de relancer l'erreur pour que le composant puisse la gérer (ex: afficher un toast)
    throw new Error("Impossible d'envoyer le message à l'élève.");
  }
};

export interface Mail {
  id: string;
  delivery?: {
    info?: {
      messageId?: string;
    };
    startTime?: any;
    state?: string;
    attempts?: number;
  };
  to: string[];
  from?: string;
  replyTo?: string;
  userId?: string | number;
  firstMessage?: boolean;
  originId?: string;
  read?: boolean;
  status?: string;
  message: {
    subject: string;
    text: string;
    html: string;
    headers?: { key: string; value: string }[];
    messageId?: string;
    status?: string;
  };
  createdAt?: any;
  type?: string;
}

export const subscribeToUserMessages = (userId: string, callback: (mails: Mail[]) => void) => {
  const mailsRef = collection(db, "mail");
  const q = query(
    mailsRef,
    where("userId", "==", userId)
  );

  return onSnapshot(q, (snapshot) => {
    const mails = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    } as Mail));
    callback(mails);
  });
};

export const sendReply = async (replyData: any) => {
  const mailsRef = collection(db, "mail");
  await addDoc(mailsRef, {
    ...replyData,
    createdAt: serverTimestamp(),
    delivery: {
      state: "PENDING",
      startTime: serverTimestamp(),
      attempts: 0
    }
  });
};

export const closeMailThread = async (mailId: string) => {
  const mailRef = doc(db, "mail", mailId);
  await updateDoc(mailRef, {
    "status": "closed"
  });
};

const firebaseService = {
  sendContactMessage,
  updateUserActivity,
  getUserActivity,
  sendStudentMessage,
  subscribeToUserMessages,
  sendReply,
  closeMailThread
};
export default firebaseService;